import { logger } from '@/core/logger';
import { useAppStore } from '@/core/stores/appStore';
import { formatEmiratesId } from '@/core/utils/validation';
import { RegistrationStorage } from '@/modules/onboarding/registration/storage/registrationStorage';
import { customerDetailsService } from '@/modules/onboarding/services/customerDetailsService';
import { useCallback, useEffect, useRef, useState } from 'react';

export interface UseRegistrationStatusResult {
    isLoading: boolean;
    error: string | null;
    registrationStatus: string | null;
    message: string | null;
    refresh: () => void;
}

/**
 * Polling interval in milliseconds (5 seconds)
 */
const POLLING_INTERVAL = 5000;

/**
 * Registration statuses that indicate completion (stop polling)
 */
const COMPLETED_STATUSES = ['approved', 'rejected', 'completed'];

/**
 * Hook to fetch registration status with polling
 * Checks both appStore and AsyncStorage for registration IDs
 * Supports both UAE (EID) and International (Passport) flows
 * Provides a refresh function to manually trigger API call
 * Automatically polls for status updates until registration is complete
 */
export function useRegistrationStatus(): UseRegistrationStatusResult {
    const uaePassRegistration = useAppStore((state) => state.uaePassRegistration);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [registrationStatus, setRegistrationStatus] = useState<string | null>(null);
    const [message, setMessage] = useState<string | null>(null);
    const [refreshTrigger, setRefreshTrigger] = useState(0);
    const isFetchingRef = useRef(false);
    const pollingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const shouldPollRef = useRef(true);
    const hasVerifiedOnceRef = useRef(false); // Track if we've verified status at least once via API

    const fetchRegistrationStatus = useCallback(async () => {
        // Try to get data from appStore first
        let registrationId = uaePassRegistration?.registrationId;
        let customerId = uaePassRegistration?.customerId;
        let idn = uaePassRegistration?.user?.idn;
        let passportNumber: string | undefined;
        let nationality = uaePassRegistration?.user?.nationalityEN;

        // If appStore doesn't have the data, try AsyncStorage as fallback
        if (!registrationId || !customerId || (!idn && !passportNumber) || !nationality) {
            logger.debug('useRegistrationStatus', 'Missing data in appStore, checking AsyncStorage');
            const storedIds = await RegistrationStorage.getRegistrationIds();
            if (storedIds) {
                registrationId = registrationId || storedIds.registrationId;
                customerId = customerId || storedIds.customerId;
                idn = idn || storedIds.idn;
                passportNumber = passportNumber || storedIds.passportNumber;
                nationality = nationality || storedIds.nationality;
                logger.debug('useRegistrationStatus', 'Retrieved registration IDs from AsyncStorage');
            }
        }

        // Only skip API call if we've already verified once AND status is approved for THIS registration
        // This ensures we always call the API at least once to verify current status
        // AND validates that cached status belongs to current registration
        if (hasVerifiedOnceRef.current && registrationId) {
            const savedStatus = await RegistrationStorage.getRegistrationStatus(registrationId);
            if (savedStatus === 'approved') {
                logger.info('useRegistrationStatus', 'Registration already approved (from storage, validated)', {
                    savedStatus,
                    registrationId,
                });
                setRegistrationStatus('approved');
                setMessage('Your application status is Approved');
                shouldPollRef.current = false;
                if (pollingIntervalRef.current) {
                    clearInterval(pollingIntervalRef.current);
                    pollingIntervalRef.current = null;
                }
                return;
            }
        }

        // Determine which ID type we have (EID or PASSPORT)
        const hasIdn = !!idn && idn.trim() !== '';
        const hasPassportNumber = !!passportNumber && passportNumber.trim() !== '';

        // Only call API if we have all required data (either EID or passport number)
        if (!registrationId || !customerId || (!hasIdn && !hasPassportNumber) || !nationality) {
            logger.debug('useRegistrationStatus', 'Missing required data, skipping API call', {
                hasRegistrationId: !!registrationId,
                hasCustomerId: !!customerId,
                hasIdn,
                hasPassportNumber,
                hasNationality: !!nationality,
            });
            return;
        }

        // Prevent duplicate calls
        if (isFetchingRef.current) {
            logger.debug('useRegistrationStatus', 'API call already in progress, skipping');
            return;
        }

        isFetchingRef.current = true;
        setIsLoading(true);
        setError(null);

        try {
            // Determine ID type and value based on what's available
            const idType = hasIdn ? 'EID' : 'PASSPORT';
            const idValue = hasIdn 
                ? formatEmiratesId(idn) // Format Emirates ID if available
                : (passportNumber?.trim().toUpperCase() || ''); // Use passport number if available

            logger.debug('useRegistrationStatus', 'Fetching registration status', {
                registrationId,
                customerId,
                idType,
                idValue: hasIdn ? formatEmiratesId(idn) : '***', // Mask passport number in logs
                nationality,
            });

            const response = await customerDetailsService.getRegistrationStatus({
                registration_id: registrationId,
                id_type: idType,
                id_value: idValue,
                nationality,
                customer_id: customerId,
            });

            const status = response.data.registration_status?.toLowerCase();
            const statusMessage = response.data.message;

            logger.info('useRegistrationStatus', 'Registration status fetched successfully', {
                status,
                message: statusMessage,
            });

            setRegistrationStatus(status);
            setMessage(statusMessage);
            
            // Mark that we've verified status at least once via API
            hasVerifiedOnceRef.current = true;

            // If status is approved, save it with registration_id to avoid redundant API calls in future
            // The registration_id ensures cached status is only used for the same registration
            if (status === 'approved' && registrationId) {
                try {
                    await RegistrationStorage.setRegistrationStatus('approved', registrationId);
                    logger.info('useRegistrationStatus', 'Approved status saved to storage with registration_id', {
                        registrationId,
                    });
                    
                    // Determine registration type BEFORE clearing data
                    const { useManualRegistrationStore } = await import('@/modules/onboarding/registration/store/registrationStore');
                    const { useAppStore } = await import('@/core/stores/appStore');
                    const manualRegistration = useManualRegistrationStore.getState().registrationResponse;
                    const uaePassRegistration = useAppStore.getState().uaePassRegistration;
                    
                    // Save registration type before clearing data
                    const registrationType = (manualRegistration && !uaePassRegistration) ? 'manual' : 'uae-pass';
                    await RegistrationStorage.setRegistrationType(registrationType);
                    logger.info('useRegistrationStatus', 'Registration type saved before clearing data', { registrationType });
                    
                    // Clear all registration data when approved (except status which is kept for validation)
                    try {
                        await useManualRegistrationStore.getState().clearAll();
                        useAppStore.getState().clearUaePassRegistration();
                        await RegistrationStorage.clearReachedRegistrationSuccess();
                        await RegistrationStorage.clearRegistrationIds();
                        // Note: We keep registration status in storage for validation, it will be cleared on sign in
                        logger.info('useRegistrationStatus', 'All registration data cleared after approval (status kept for validation)');
                    } catch (clearError) {
                        logger.error('useRegistrationStatus', 'Failed to clear registration data after approval', clearError);
                    }
                } catch (saveError) {
                    logger.error('useRegistrationStatus', 'Failed to save approved status', saveError);
                }
            }

            // Stop polling if registration is complete
            if (status && COMPLETED_STATUSES.includes(status)) {
                logger.debug('useRegistrationStatus', 'Registration completed, stopping polling', { status });
                shouldPollRef.current = false;
                if (pollingIntervalRef.current) {
                    clearInterval(pollingIntervalRef.current);
                    pollingIntervalRef.current = null;
                }
            }
        } catch (err: any) {
            logger.error('useRegistrationStatus', 'Failed to fetch registration status', err);
            setError(err?.message || 'Failed to fetch registration status');
            // Don't stop polling on error - continue polling to retry
        } finally {
            setIsLoading(false);
            isFetchingRef.current = false;
        }
    }, [uaePassRegistration]);

    // Set up polling when component mounts or dependencies change
    useEffect(() => {
        // Reset polling flag when dependencies change
        shouldPollRef.current = true;

        // Initial fetch
        fetchRegistrationStatus();

        // Set up polling interval
        if (shouldPollRef.current) {
            pollingIntervalRef.current = setInterval(() => {
                if (shouldPollRef.current && !isFetchingRef.current) {
                    logger.debug('useRegistrationStatus', 'Polling: Fetching registration status');
                    fetchRegistrationStatus();
                }
            }, POLLING_INTERVAL);
        }

        // Cleanup polling on unmount or when dependencies change
        return () => {
            if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current);
                pollingIntervalRef.current = null;
            }
        };
    }, [fetchRegistrationStatus, refreshTrigger]);

    // Refresh function to manually trigger API call and restart polling
    const refresh = useCallback(() => {
        logger.debug('useRegistrationStatus', 'Manual refresh triggered');
        hasVerifiedOnceRef.current = false; // Reset verification flag to force API call
        shouldPollRef.current = true; // Restart polling
        setRefreshTrigger((prev) => prev + 1);
    }, []);

    return {
        isLoading,
        error,
        registrationStatus,
        message,
        refresh,
    };
}

